import axios from 'axios';
import type {
  ProductCombinationInput,
  tempCreateProductInput,
} from '_graphql/__generated__/globalTypes';

const MEDIA_UPLOAD_PATH = '/api/v1/soko/media/upload/url';

/**
 * Dev: absolute same-origin URL so this request ignores `axios.defaults.baseURL`
 * (often set to the media host in upload components). Prod: `REACT_APP_ASSET_URI`.
 */
function bulkUrlUploadEndpoint(): string {
  // if (process.env.NODE_ENV === 'development') {
  //   if (typeof window !== 'undefined') {
  //     return `${window.location.origin}/__media_proxy__${MEDIA_UPLOAD_PATH}`;
  //   }
  //   return `/__media_proxy__${MEDIA_UPLOAD_PATH}`;
  // }
  const root = (
    process.env.REACT_APP_ASSET_URI!
  ).replace(/\/+$/, '');
  return `${root}${MEDIA_UPLOAD_PATH}`;
}

function isRehostableHttpUrl(url: string): boolean {
  const t = url.trim();
  if (!t) {
    return false;
  }
  try {
    const u = new URL(t);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function extractUploadedImageUrl(responseBody: unknown): string | null {
  if (responseBody == null) {
    return null;
  }
  if (typeof responseBody === 'string') {
    const s = responseBody.trim();
    return s.startsWith('http') ? s : null;
  }
  const root = responseBody as Record<string, unknown>;
  const layer1 =
    root.data !== undefined && typeof root.data !== 'string'
      ? (root.data as Record<string, unknown>)
      : root;
  const layer2 =
    layer1 &&
      typeof layer1 === 'object' &&
      layer1.data !== undefined &&
      typeof layer1.data !== 'string'
      ? (layer1.data as Record<string, unknown>)
      : null;

  const tryVal = (v: unknown): string | null => {
    if (typeof v !== 'string') {
      return null;
    }
    const s = v.trim();
    return s.startsWith('http') ? s : null;
  };

  const candidates = [
    layer2 && tryVal(layer2.imageLink),
    layer2 && tryVal(layer2.url),
    layer2 && tryVal(layer2.location),
    tryVal(layer1.imageLink),
    tryVal(layer1.url),
    tryVal(layer1.location),
    tryVal(root.url),
  ].filter(Boolean) as string[];

  return candidates[0] ?? null;
}

function collectUniqueHttpImageUrls(products: tempCreateProductInput[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  const add = (u: string) => {
    const t = u.trim();
    if (!isRehostableHttpUrl(t) || seen.has(t)) {
      return;
    }
    seen.add(t);
    ordered.push(t);
  };

  for (const p of products) {
    add(p.avatar);
    for (const m of p.media ?? []) {
      if (m) {
        add(m);
      }
    }
    for (const c of p.combinations ?? []) {
      if (!c) {
        continue;
      }
      add(c.avatar);
      for (const m of c.media ?? []) {
        if (m) {
          add(m);
        }
      }
    }
  }
  return ordered;
}

function mapCombination(
  c: ProductCombinationInput,
  urlMap: Map<string, string>,
): ProductCombinationInput {
  const mapOne = (u: string) => urlMap.get(u.trim()) ?? u;
  return {
    ...c,
    avatar: mapOne(c.avatar),
    media: c.media?.map((m) => (m ? mapOne(m) : m)) ?? null,
  };
}

function mapProduct(
  p: tempCreateProductInput,
  urlMap: Map<string, string>,
): tempCreateProductInput {
  const mapOne = (u: string) => urlMap.get(u.trim()) ?? u;
  return {
    ...p,
    avatar: mapOne(p.avatar),
    media: (p.media ?? []).map(mapOne),
    combinations: p.combinations?.map((c) => (c ? mapCombination(c, urlMap) : c)),
  };
}

export type RehostBulkProductMediaOptions = {
  getHeaders: () => Record<string, string>;
  folder?: string;
};

export async function rehostBulkProductMediaUrls(
  products: tempCreateProductInput[],
  options: RehostBulkProductMediaOptions,
): Promise<
  { ok: true; products: tempCreateProductInput[] } | { ok: false; message: string }
> {
  const unique = collectUniqueHttpImageUrls(products);
  if (unique.length === 0) {
    return { ok: true, products };
  }

  const urlMap = new Map<string, string>();
  const endpoint = bulkUrlUploadEndpoint();

  for (const sourceUrl of unique) {
    try {
      const { data } = await axios.post(
        endpoint,
        { url: sourceUrl, ...(options.folder ? { folder: options.folder } : {}) }
      );
      const next = extractUploadedImageUrl(data);
      if (!next) {
        return {
          ok: false,
          message: `Image upload succeeded but no URL was returned for: ${sourceUrl}`,
        };
      }
      urlMap.set(sourceUrl, next);
    } catch (e) {
      let msg = 'Request failed';
      if (axios.isAxiosError(e)) {
        const body = e.response?.data as Record<string, unknown> | undefined;
        const fromBody =
          body &&
            typeof body.message === 'string'
            ? body.message
            : body && typeof body.error === 'string'
              ? body.error
              : null;
        msg = fromBody ?? e.message;
      } else if (e && typeof e === 'object' && 'message' in e) {
        msg = String((e as { message: unknown }).message);
      }
      return {
        ok: false,
        message: `Failed to upload image ${sourceUrl}: ${msg}`,
      };
    }
  }

  return {
    ok: true,
    products: products.map((p) => mapProduct(p, urlMap)),
  };
}
